import React, { useCallback, useEffect, useState } from 'react';
import cn from 'classnames';
import { faCheck } from '@fortawesome/pro-light-svg-icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next/';
import { observer } from 'mobx-react-lite';

import PersonalService from '@services/PersonalService';
import { useDebounce } from '@hooks/useDebounce';

import { useStores } from '@models/RootStore/RootStore';

import { Country } from './AddVisitedCountryBlock.props';
import styles from './AddVisitedCountryBlock.module.scss';
import BucketListBlockItem from '@components/ui/BucketListBlockItem/BucketListBlockItem';
import CountrySearchInputWithResults from '@components/ui/CountrySearchInputWithResults/CountrySearchInputWithResults';
import TitleCard from '@components/ui/TitleCard/TitleCard';

const AddVisitedCountryBlock = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [countries, setCountries] = useState<Country[]>([]);
  const [loadingId, setLoadingId] = useState('');
  const [isSearching, setSearching] = useState(false);
  const { UserStore } = useStores();

  const definitiveSearchQuery = useDebounce(searchQuery, 200);

  const handleQuery = async () => {
    if (definitiveSearchQuery === '') {
      setCountries([]);

      return;
    }
    setSearching(true);

    const results = await PersonalService.searchCountries(definitiveSearchQuery);

    setSearching(false);
    setCountries(results || []);
  };

  useEffect(() => {
    handleQuery();
  }, [definitiveSearchQuery]);

  const mappedCountries = countries.map((dataPoint: Country) => {
    dataPoint.selected = UserStore.hasVisited(dataPoint._id);
    dataPoint.isLoading = dataPoint._id === loadingId;

    return dataPoint;
  });

  const getResults = useCallback(() => {
    return definitiveSearchQuery
      ? mappedCountries
          .filter((dataPoint) => {
            if (!dataPoint.title) return false;

            const dataTitle = dataPoint.title.toLowerCase();
            const toSearch = definitiveSearchQuery.replace(/ /g, '').toLowerCase();

            return dataTitle.includes(toSearch);
          })
          .sort((a: any, b: any) => {
            if (a.title < b.title) return -1;
            if (a.title > b.title) return 1;
            return 0;
          })
      : [];
  }, [definitiveSearchQuery, countries]);

  const handleResultSelect = async (selected: Country) => {
    setLoadingId(selected._id);

    await UserStore.toggleVisited(selected._id);

    setLoadingId('');

    if (UserStore.hasVisited(selected._id)) {
      toast.success(t('DOMAIN.BUCKETLIST.ADD_TO_VISITED_SUCCESS', { name: selected.title }));
    } else {
      toast.success(t('DOMAIN.BUCKETLIST.REMOVED_FROM_VISITED_SUCCESS', { name: selected.title }));
    }
  };

  const RenderListItems = () => {
    return UserStore.visitedTips.map((item) => (
      <BucketListBlockItem key={item.id} {...item} className="mb-ms" />
    ));
  };

  return (
    <TitleCard
      title={t('DOMAIN.ACCOUNT.ADD_VISITED_COUNTRYBLOCK_TITLE')}
      icon={faCheck}
      titleStyle="FullWidth"
      size="sm"
      className={styles.TitleCard}>
      <div className={styles.AddVisitedCountryBlock}>
        <CountrySearchInputWithResults
          isLoading={isSearching}
          name="countrysearch"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          results={getResults()}
          onSelect={handleResultSelect}
          className={cn(styles.SearchInput, { [styles.Searching]: !!definitiveSearchQuery })}
        />
        <div className={cn(styles.VisitedList, { [styles.Searching]: !!definitiveSearchQuery })}>
          {!definitiveSearchQuery && RenderListItems()}
        </div>
      </div>
    </TitleCard>
  );
};

export default observer(AddVisitedCountryBlock);
